<template>
  <panel
    class="case-questions-container new-globals"
    color="blue-3"
    title="Dialogue"
    scroll-mode="content"
  >
    <div class="add-question-button-container">
      <md-button
        :disabled="readonly"
        class="md-raised md-primary add-question-button"
        @click="state.showAddQuestionDialog = true"
      >
        <md-icon>add</md-icon>Add Saved Dialogue
      </md-button>
      <help-tooltip type="info">
        Select from a list of
        <br>existing questions.
      </help-tooltip>
    </div>

    <help-tooltip v-if="errors.length" type="error">
      <span v-for="error in errors" :key="error.message">
        {{ error.message }}
        <br>
      </span>
    </help-tooltip>

    <div class="tabs-container">
      <tab-dropdown
        label="PATIENT"
        :items="patientItems"
        :selected="state.activeId === 'PATIENT'"
        @selectItem="onSelectPatientItem"
      />
      <tab-dropdown
        v-for="call in caseConf.dialogue.calls"
        :key="call.id"
        :label="call.callee.en"
        :items="calleeItems"
        :selected="state.activeId === call.id"
        @selectItem="itemKey => onSelectCalleeItem(call.id, itemKey)"
      />

      <md-field v-if="state.showNewCallInput" md-counter class="new-call-input">
        <md-input
          ref="newCallInput"
          type="text"
          :value="state.newCallCallee"
          :maxlength="15"
          @input="(name) => updateNewCallCallee(name)"
          @keypress.enter="submitNewCall"
        />
        <md-button class="md-icon-button md-mini" @click="submitNewCall">
          <md-icon>add</md-icon>
        </md-button>
      </md-field>
      <div>
        <md-button
          :disabled="readonly || (caseConf.dialogue.calls && caseConf.dialogue.calls.length >= 7)"
          class="md-raised md-primary add-call-option-button"
          @click="showNewCallInput()"
        >
          <md-icon>add</md-icon>Add Call Option
        </md-button>
        <help-tooltip v-if="(caseConf.dialogue.calls && caseConf.dialogue.calls.length >= 7)" type="warning">
          You can have up to 7 call options
        </help-tooltip>
      </div>
    </div>

    <div v-show="state.activeId === 'PATIENT' && state.activeTab === DialogueType.Q_AND_A" class="non-calls-container">
      <dialogue-editor
        :key="'PATIENT_QANDA'"
        :settings="questionSettings"
        :items="caseConf.dialogue.questions || []"
        :readonly="readonly"
        title-prefix="Patient: "
        @update="updateQuestions"
        @playAudio="playAnswerText"
      />
    </div>
    <div v-show="state.activeId === 'PATIENT' && state.activeTab === DialogueType.COMMANDS" class="non-calls-container">
      <dialogue-editor
        :key="'PATIENT_COMMANDS'"
        :settings="commandSettings"
        :items="caseConf.dialogue.commands || []"
        :readonly="readonly"
        title-prefix="Patient: "
        @update="updateCommands"
        @playAudio="playAnswerText"
      />
    </div>
    <div v-show="state.activeId === 'PATIENT' && state.activeTab === DialogueType.UNSOLICITED" class="non-calls-container">
      <dialogue-editor
        :key="'PATIENT_UNSOLICITED'"
        :settings="unsolicitedSettings"
        :items="caseConf.dialogue.unsolicited || []"
        :readonly="readonly"
        title-prefix="Patient: "
        @update="updateUnsolicited"
        @playAudio="playAnswerText"
      />
    </div>

    <div v-show="state.activeId && state.activeId !== 'PATIENT'" class="calls-container">
      <div v-for="call in caseConf.dialogue.calls" v-show="state.activeId === call.id" :key="call.id" class="call-container">
        <div class="call-action-buttons">
          <div class="gender-switch-container md-switch">
            <label class="md-switch-label left">Female</label>
            <md-switch
              class="md-primary gender-switch"
              :value="getVoiceGender(call.calleeVoiceId) === 'MALE' ? true : false"
              :disabled="readonly"
              @change="toggled => toggled ? changeVoice(call.id, 'MALE') : changeVoice(call.id, 'FEMALE')"
            >
              Male
            </md-switch>
          </div>
          <md-button
            class="md-icon-button md-accent md-raised"
            :disabled="readonly"
            @click="promptCallRemove(call)"
          >
            <md-icon> close </md-icon>
          </md-button>
        </div>
        <dialogue-editor
          v-show="state.activeId === call.id && state.activeTab === DialogueType.Q_AND_A"
          id="call-questions"
          :key="`${call.id}_QANDA`"
          :settings="callSettings"
          :items="call.items || []"
          :readonly="readonly"
          :title-prefix="call.callee.en + ': '"
          @update="(items) => updateCallItems(call.id, items)"
          @playAudio="(item) => playAnswerText(item, call.calleeVoiceId)"
        />
        <dialogue-editor
          v-show="state.activeId === call.id && state.activeTab === DialogueType.UNSOLICITED"
          id="call-unsolicited"
          :key="`${call.id}_UNSOLICITED`"
          :settings="callUnsolicitedSettings"
          :items="call.unsolicited || []"
          :readonly="readonly"
          :title-prefix="call.callee.en + ': '"
          @update="(items) => updateCallUnsolicited(call.id, items)"
          @playAudio="(item) => playAnswerText(item, call.calleeVoiceId)"
        />
      </div>
    </div>

    <md-dialog
      :md-active.sync="state.showAddQuestionDialog"
      :md-fullscreen="false"
      class="add-questions-dialog gray-3 bg"
    >
      <section-title>ADD NEW: Q&A</section-title>
      <questions-selector
        :questions="unusedQuestions"
        @updateSelectedQuestions="
          (questions) => updateSelectedQuestions(questions)
        "
      />
      <md-dialog-actions>
        <md-button
          class="md-primary"
          @click="state.showAddQuestionDialog = false"
        >
          Cancel
        </md-button>
        <md-button class="md-raised md-primary" @click="addSelectedQuestions()">
          Add to List
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog-confirm
      :md-active.sync="state.callRemovalConfirmOpen"
      md-title="Remove call"
      :md-content="state.pendingRemovalCall ? `Are you sure you want to remove the call option to ${state.pendingRemovalCall.callee.en} ?` : ''"
      md-confirm-text="Remove"
      md-cancel-text="Cancel"
      @md-cancel="cancelPendingCallRemoval"
      @md-confirm="removePendingRemovalCall"
    />
  </panel>
</template>

<script>
import uniqBy from 'lodash/uniqBy';
import get from 'lodash/get';
import sample from 'lodash/sample';
import Chance from 'chance';

import HelpTooltip from '~lib/common/components/HelpTooltip.vue';
import voiceIdEngines from '~lib/common/config/voiceIdEngines';

import Panel from '../common/Panel';
import SectionTitle from '../common/SectionTitle';
import QuestionsSelector from './QuestionsSelector';
import DialogueEditor from './DialogueEditor.vue';
import TabDropdown from './TabDropdown.vue';
import servicesClient from '../../providers/servicesClient';

const chance = new Chance();

const maleVoices = ['Brian', 'Joey', 'Kevin', 'Matthew'];
const femaleVoices = ['Amy', 'Emma', 'Joanna', 'Kendra', 'Kimberly', 'Salli']

const DialogueType = {
  Q_AND_A: 'Q_AND_A',
  COMMANDS: 'COMMANDS',
  UNSOLICITED: 'UNSOLICITED',
};

export default {
  name: 'case-questions',
  components: {
    Panel,
    SectionTitle,
    HelpTooltip,
    QuestionsSelector,
    DialogueEditor,
    TabDropdown,
  },
  props: {
    caseConf: {
      type: Object,
      default: () => ({
        language: 'en',
      }),
    },
    questions: {
      type: Array,
      default: () => [],
    },
    answers: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
    },
    savingItemId: {
      type: String,
      default: null,
    },
    user: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      questionSettings: {
        type: DialogueType.Q_AND_A,
        onText: 'On at start questions',
        offText: 'Off at start questions',
        questionLabel: 'Question',
        answerLabel: 'Answer',
        newQuestionLabel: 'Type here to add a new question',
        newAnswerLabel: 'Type here to add a new answer',
      },
      commandSettings: {
        type: DialogueType.COMMANDS,
        onText: 'On at start commands',
        offText: 'Off at start commands',
        onSubtitle: '(Commands need to be connected to their desired effects in Case Logic)',
        offSubtitle: ' ',
        questionLabel: 'Command',
        newQuestionLabel: 'Type here to add a new command',
        isAnswerDisabled: true,
      },
      unsolicitedSettings: {
        type: DialogueType.UNSOLICITED,
        offText: 'Unsolicited',
        offSubtitle: '(Unsolicited needs to be triggered by an action or timer in Case Logic)',
        onDisabled: true,
        isQuestionDisabled: true,
        answerLabel: 'Patient Unsolicited',
        newAnswerLabel: 'Type here to add a new patient dialogue'
      },
      callSettings: {
        onText: 'On at start call dialogue',
        offText: 'Off at start call dialogue',
        questionLabel: 'Dialogue',
        answerLabel: 'Response',
        newQuestionLabel: 'Type here to add a new dialogue',
        newAnswerLabel: 'Type here to add a new response',
      },
      callUnsolicitedSettings: {
        type: DialogueType.UNSOLICITED,
        offText: 'Callee Unsolicited',
        offSubtitle: '(Unsolicited needs to be triggered by an action or timer in Case Logic)',
        onDisabled: true,
        isQuestionDisabled: true,
        answerLabel: 'Callee Unsolicited',
        newAnswerLabel: 'Type here to add a new callee dialogue'
      },
      patientItems: [
        { key: DialogueType.Q_AND_A, label: 'PATIENT Q & A' },
        { key: DialogueType.COMMANDS, label: 'PATIENT COMMANDS' },
        { key: DialogueType.UNSOLICITED, label: 'PATIENT UNSOLICITED' },
      ],
      calleeItems: [
        { key: DialogueType.Q_AND_A, label: 'CALLEE Q & A' },
        { key: DialogueType.UNSOLICITED, label: 'CALLEE UNSOLICITED' },
      ],
      state: {
        activeId: 'PATIENT',
        activeTab: DialogueType.Q_AND_A,
        showAddQuestionDialog: false,
        selectedQuestions: [],
        showNewCallInput: false,
        newCallCallee: '',
        pendingRemovalCall: null,
        callRemovalConfirmOpen: false,
      },
      DialogueType,
    };
  },
  computed: {
    errors() {
      const errors = get(
        this.$store.getters['validation/errorsByField'],
        'dialogue',
        []
      );
      return uniqBy(errors, 'message');
    },
    warnings() {
      const warnings = get(
        this.$store.getters['validation/visibleWarningsByField'],
        'dialogue',
        []
      );
      return uniqBy(warnings, 'message');
    },
    unusedQuestions() {
      return this.questions.filter((q) => {
        return !(this.caseConf.dialogue.questions || []).some(
          (question) => q.content.en === question.content.en
        );
      });
    },
  },
  methods: {
    updateSelectedQuestions(questions) {
      this.state.selectedQuestions = questions;
    },
    async addSelectedQuestions() {
      this.$eventHub.$emit('DIALOGUE::ADD', this.state.selectedQuestions);
      this.$analytics.event('Authoring Tool', 'questions_add_new_from_list', null, this.state.selectedQuestions.length);
      this.state.selectedQuestions = [];
      this.state.showAddQuestionDialog = false;
    },
    async playAnswerText(question, withVoiceId) {
      const text = question.options.answer[this.caseConf.language];
      const voiceId =
        withVoiceId ||
        get(this.caseConf, `_avatar.pollyVoiceId.${this.caseConf.language}`);
      const engine = voiceIdEngines[voiceId];
      const response = await servicesClient.post('generate-audio', {
        text,
        voiceId,
        engine
      });
      const audio = new Audio(response.data.s3Location);
      audio.play();
    },
    showTab(entityId, dialogueType) {
      this.state.activeTab = dialogueType;
      this.state.activeId = entityId;
    },
    updateQuestions(items) {
      this.$emit('update', {
        dialogue: {
          questions: items,
        },
      });
    },
    updateCommands(items) {
      this.$emit('update', {
        dialogue: {
          commands: items,
        },
      });
    },
    showNewCallInput() {
      this.state.showNewCallInput = true;
      this.$nextTick(() => {
        const newCallInputRef = this.$refs.newCallInput;
        if (newCallInputRef && newCallInputRef.$el) {
          newCallInputRef.$el.focus();
        }
      })
    },
    updateNewCallCallee(name) {
      this.state.newCallCallee = name;
    },
    submitNewCall() {
      if (!this.state.newCallCallee) {
        return;
      }
      const callId = chance.guid();
      const newCall = {
        id: callId,
        callee: {
          en: this.state.newCallCallee,
        },
        calleeVoiceId: sample([...maleVoices, ...femaleVoices]),
        items: [],
      };
      this.caseConf.dialogue.calls = this.caseConf.dialogue.calls || [];
      this.caseConf.dialogue.calls.push(newCall)
      this.$emit('update', {
        dialogue: {
          calls: this.caseConf.dialogue.calls,
        },
      });
      this.state.newCallCallee = '';
      this.state.showNewCallInput = false;
      this.showTab(callId, DialogueType.Q_AND_A);
    },
    updateCallItems(callId, items) {
      this.$emit('update', {
        dialogue: {
          calls: (this.caseConf.dialogue.calls || []).map((call) =>
            call.id === callId
              ? {
                  ...call,
                  items,
                }
              : call
          ),
        },
      });
    },
    updateCallUnsolicited(callId, unsolicited) {
      this.$emit('update', {
        dialogue: {
          calls: (this.caseConf.dialogue.calls || []).map((call) =>
            call.id === callId
              ? {
                  ...call,
                  unsolicited,
                }
              : call
          ),
        },
      });
    },
    promptCallRemove(call) {
      this.state.pendingRemovalCall = call;
      this.state.callRemovalConfirmOpen = true;
    },
    removePendingRemovalCall() {
      const { pendingRemovalCall } = this.state;
      this.$emit('update', {
        dialogue: {
          calls: (this.caseConf.dialogue.calls || []).filter(
            (c) => c.id !== pendingRemovalCall.id
          ),
        },
      });
      this.showTab('PATIENT', DialogueType.Q_AND_A);
      this.state.callRemovalConfirmOpen = false;
      this.state.pendingRemovalCall = null;
    },
    updateUnsolicited(items) {
      this.$emit('update', {
        dialogue: {
          unsolicited: items,
        }
      });
    },
    cancelPendingCallRemoval() {
      this.state.callRemovalConfirmOpen = false;
      this.state.pendingRemovalCall = null;
    },
    getVoiceGender(voiceId) {
      return maleVoices.includes(voiceId) ? 'MALE' : 'FEMALE';
    },
    changeVoice(callId, gender) {
      const genderVoices = gender === 'MALE' ? maleVoices : femaleVoices;
      const caseAvatarVoice = get(
        this.caseConf,
        `_avatar.pollyVoiceId.${this.caseConf.language}`
      );
      const availableVoices = genderVoices.filter(
        (voiceId) =>
          voiceId !== caseAvatarVoice &&
          !this.caseConf.dialogue.calls.find(
            (call) => call.calleeVoiceId === voiceId
          )
      );
      const randomVoice = sample(availableVoices)
      this.$emit('update', {
        dialogue: {
          calls: (this.caseConf.dialogue.calls || []).map((call) =>
            call.id === callId
              ? {
                  ...call,
                  calleeVoiceId: randomVoice,
                }
              : call
          ),
        },
      });
    },
    onSelectPatientItem(itemKey) {
      this.showTab('PATIENT', itemKey);
    },
    onSelectCalleeItem(callId, itemKey) {
      this.showTab(callId, itemKey);
    },
  },
};
</script>

<style scoped>
.case-questions-container {
  font-size: 16px;
  height: 100%;
}

.section-title {
  margin-bottom: 10px;
}

.tabs-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 20px;
}

.md-tabs >>> .md-tabs-navigation .md-button {
  font-size: 16px;
}

.new-call-input {
  width: 150px;
  height: 36px;
  min-height: unset;
  margin-bottom: 0;
  margin-top: 0;
  margin-left: 24px;
  margin-right: 24px;
  padding: 4px 10px !important;
}

.new-call-input input {
  width: 100%;
}

.new-call-input .md-button {
  position: absolute;
  right: 5px;
  top: 0px;
}

.add-question-button-container {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.add-question-button,
.add-call-option-button {
  margin: 0;
  margin-right: 10px;
  text-transform: uppercase;
  border-radius: 16px;
}

.add-call-option-button {
  margin-left: 10px;
}

.call-action-buttons {
  display: flex;
  justify-content: space-between;
}

.non-calls-container {
  height: calc(100% - 80px);
}

.calls-container {
  height: calc(100% - 120px);
}

.call-container {
  height: 100%;
}

.gender-switch-container {
  display: flex;
  align-items: center;
  margin: 0;
}

.gender-switch.md-checked >>> .md-switch-container {
  background-color: var(--md-theme-default-primary-on-, rgba(68,138,255,0.38));
}

.gender-switch.md-checked >>> .md-switch-thumb {
  background-color: var(--md-theme-default-primary, #448aff);
}

.gender-switch >>> .md-switch-container {
  background-color: var(--md-theme-default-light-accent-on-,rgba(255,82,82,.38));
}

.gender-switch >>> .md-switch-thumb {
  background-color: var(--md-theme-default-light-accent,#ff5252);
}

.md-switch-label.left {
  padding-left: 0;
  padding-right: 16px;
}

.add-questions-dialog {
  padding: 20px 35px;
  overflow: auto;
}

@media (max-width: 599px) {
  .add-questions-dialog {
    width: 400px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .add-questions-dialog {
    width: 600px;
  }
}

@media (min-width: 960px) {
  .add-questions-dialog {
    width: 800px;
  }
}

.background-blue {
  background-color: #3f83c5;
}

.tab-dropdown {
  margin-right: 8px;
}
</style>
